import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createAssistant,
  getAssistantById,
  getKnowledgeStatus,
} from "../../../actions/assistants";
import KnowledgeProgress from "./KnowledgeProgress";
import {
  GlobeIcon,
  RobotIcon,
  ArrowIcon,
  DocIcon,
  ChevronRightIcon,
  CheckIcon,
  ClockIcon,
  SparkleIcon,
  StarIcon,
  ShieldIcon,
  PhoneIcon,
  LockIcon,
  CodeIcon,
  SlidersIcon,
  UploadIcon,
  CloseIcon,
  formatFileSize,
} from "./icons";
import { screenshotSrc } from "../../../utils/screenshot";

// Uploading documents has no endpoint behind it yet: the dropzone collected
// files that were never sent anywhere. Until it does, the button says so
// rather than opening something that cannot work. Flip this to bring the
// panel below back.
const MANUAL_UPLOAD_READY = false;

// The crawl runs server-side once POST /assistants succeeds, so this step
// holds and polls GET /assistants/:id until the status leaves "crawling".
const POLL_INTERVAL_MS = 5000;

// Cosmetic only: the API reports a single "crawling" status, so the three
// The checklist beside the form still walks forward on a timer - it is three
// summary lines, not a report. The real stages come from knowledge-status and
// are rendered by KnowledgeProgress.
const STAGE_ADVANCE_MS = 9000;

// Statuses that mean "still working" — anything else that isn't "ready" is a
// failure worth surfacing.
export const PENDING_STATUSES = [
  "crawling",
  "pending",
  "queued",
  "processing",
];

// Requesting a screenshot per keystroke would burn the quota in seconds.
const PREVIEW_DEBOUNCE_MS = 900;

const statusSteps = [
  { title: "Reading your website", detail: "Scanning pages..." },
  { title: "Understanding your services", detail: "Analyzing your content" },
  { title: "Preparing your assistant", detail: "Building your AI receptionist" },
];

const checklist = [
  {
    title: "We'll scan your website",
    detail: "Securely read your pages and content.",
    busyDetail: "Scanning pages...",
  },
  {
    title: "Understand your business",
    detail: "Extract key info about your services, hours, and more.",
    busyDetail: "Analyzing your services, hours and content...",
  },
  {
    title: "Create your assistant",
    detail: "Build a smart AI that answers like your best team member.",
    busyDetail: "Building your AI receptionist...",
  },
];

const features = [
  { icon: <LockIcon />, title: "Secure & private", detail: "We only access what you allow." },
  { icon: <CodeIcon />, title: "No coding required", detail: "Get started in under 2 minutes." },
  { icon: <SlidersIcon />, title: "You can customize everything later", detail: "Fine-tune your assistant any time." },
];

// The API wants an absolute URL, but people type "acme.com".
const normalizeUrl = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// What goes in the mock browser's address bar - the bare host, no scheme.
const displayUrl = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  try {
    const { hostname, pathname } = new URL(normalizeUrl(trimmed));
    return `${hostname}${pathname === "/" ? "" : pathname}`;
  } catch {
    return trimmed.replace(/^https?:\/\//i, "");
  }
};

const isValidUrl = (value) => {
  try {
    const { hostname } = new URL(value);
    return hostname.includes(".");
  } catch {
    return false;
  }
};

export default function StepWebsite({ onContinue }) {
  const [manualOpen, setManualOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [crawlingId, setCrawlingId] = useState("");
  const [stage, setStage] = useState(0);
  const [shotSrc, setShotSrc] = useState("");
  // "loading" until the screenshot arrives, "failed" drops back to the mock.
  const [shotState, setShotState] = useState("idle");
  // GET /assistants/:id/knowledge-status, as it last answered: the stage, the
  // pages read against the limit, and the message to put above the bar.
  const [knowledge, setKnowledge] = useState(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const crawling = Boolean(crawlingId);
  const busy = submitting || crawling;
  const previewUrl = displayUrl(sourceUrl);

  const normalized = normalizeUrl(sourceUrl);
  const validUrl = isValidUrl(normalized) ? normalized : "";

  // A provider that can't render the site falls back to the mock skeleton.
  const showShot = Boolean(shotSrc) && shotState !== "failed";

  // Login passes a fresh arrow every render; holding it in a ref stops the
  // poll from being torn down and restarted on each parent re-render.
  const onContinueRef = useRef(onContinue);
  useEffect(() => {
    onContinueRef.current = onContinue;
  });

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList);
    if (!incoming.length) return;
    setFiles((prev) => [...prev, ...incoming]);
  };

  const handleFileInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Screenshots the site once the typed URL looks real, debounced so the
  // provider isn't hit on every keystroke.
  useEffect(() => {
    if (!validUrl) {
      setShotSrc("");
      setShotState("idle");
      return undefined;
    }

    const timer = setTimeout(() => {
      setShotSrc(screenshotSrc(validUrl));
      setShotState("loading");
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [validUrl]);

  // Poll every 5s: "crawling" keeps us on this step, "ready" moves on, and
  // anything else surfaces the crawl error.
  useEffect(() => {
    if (!crawlingId) return undefined;

    let cancelled = false;
    let timer = null;

    const stop = () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };

    const check = async () => {
      const result = await dispatch(getKnowledgeStatus(crawlingId));
      if (cancelled) return;

      // A dropped request is not an answer: keep asking rather than ending a
      // crawl that is probably still running.
      if (!result?.success) {
        timer = setTimeout(check, POLL_INTERVAL_MS);
        return;
      }

      const state = result.status;
      setKnowledge(state);

      // isLearning is the flag the API says to poll on; it covers every stage
      // of the work, not just the page reading.
      if (state.isLearning) {
        timer = setTimeout(check, POLL_INTERVAL_MS);
        return;
      }

      if (state.status === "failed") {
        stop();
        setCrawlingId("");
        setError(
          state.lastError ||
            state.message ||
            "We couldn't finish reading your website. Check the URL and try again.",
        );
        return;
      }

      // Done. The record itself is what the next step loads from, so it is
      // fetched once here rather than guessed at from this payload.
      stop();
      setStage(checklist.length);
      setCrawlingId("");

      const record = await dispatch(getAssistantById(crawlingId));
      onContinueRef.current(record?.assistant || { id: crawlingId });
    };

    timer = setTimeout(check, POLL_INTERVAL_MS);
    return stop;
  }, [crawlingId, dispatch]);

  // Walks the progress lines forward while the crawl runs, stopping one short
  // of the end so nothing reads as finished before the API says "ready".
  useEffect(() => {
    if (!crawlingId) return undefined;

    const timer = setInterval(() => {
      setStage((current) => Math.min(current + 1, checklist.length - 1));
    }, STAGE_ADVANCE_MS);

    return () => clearInterval(timer);
  }, [crawlingId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (busy) return;

    if (!name.trim()) {
      setError("Please give your assistant a name.");
      return;
    }

    const url = normalizeUrl(sourceUrl);
    if (!url || !isValidUrl(url)) {
      setError("Please enter a valid website URL.");
      return;
    }

    setError("");
    setSubmitting(true);

    const result = await dispatch(
      createAssistant({ sourceUrl: url, name: name.trim() }),
    );

    setSubmitting(false);

    if (!result?.success) {
      setError(
        result?.response?.data?.error ||
          result?.message ||
          "Something went wrong. Please try again.",
      );
      return;
    }

    const assistant = result.assistant;

    // Already crawled (a repeat submit, say) — nothing to wait for.
    if (assistant?.status === "ready") {
      onContinue(assistant);
      return;
    }

    if (!assistant?.id) {
      setError("Your assistant was created but we couldn't track its progress.");
      return;
    }

    // Crawling starts with the POST, so hold here and poll until it's ready.
    setStage(0);
    setCrawlingId(assistant.id);
  };

  return (
    <>
      <h1 className="onboarding__title">Create your AI receptionist</h1>
      <p className="onboarding__subtitle">
        Hostor will learn from your website and prepare your AI assistant automatically – no coding or technical
        setup needed.
      </p>

      <div className="onboarding__grid">
        <section className="onboarding__card">
          <h2 className="onboarding__card-title">
            {crawling ? "Reading your website" : "Tell us about your website"}
          </h2>
          <p className="onboarding__card-subtitle">
            {crawling
              ? "This takes up to a minute. Keep this page open — we'll move you on automatically."
              : "Enter your website URL and we'll handle the rest."}
          </p>

          <form
            className="onboarding__form onboarding__form--stacked"
            onSubmit={handleSubmit}
            noValidate
          >
            {error && <p className="auth-error">{error}</p>}

            <label className="onboarding__input-wrap">
              <RobotIcon />
              <input
                type="text"
                placeholder="Assistant name (e.g. Acme Support)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="onboarding__input-wrap">
              <GlobeIcon />
              <input
                type="text"
                inputMode="url"
                placeholder="https://yourwebsite.com"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                disabled={busy}
              />
            </label>
            <button
              type="submit"
              className="onboarding__btn onboarding__btn--primary"
              disabled={busy}
            >
              {crawling && (
                <span className="onboarding__btn-spinner" aria-hidden="true" />
              )}
              {crawling
                ? "Scanning your website..."
                : submitting
                  ? "Building your assistant..."
                  : "Build my assistant"}
              {!busy && <ArrowIcon />}
            </button>
          </form>

          <div className="onboarding__divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="onboarding__manual-btn"
            aria-expanded={manualOpen}
            onClick={() => setManualOpen((open) => !open)}
          >
            <DocIcon />
            <span>Add content manually</span>
            {!MANUAL_UPLOAD_READY && (
              <span className="onboarding__soon-tag">Coming soon</span>
            )}
            <ChevronRightIcon className={manualOpen ? "is-open" : ""} />
          </button>

          {manualOpen && !MANUAL_UPLOAD_READY && (
            <p className="onboarding__soon-note">
              Uploading your own documents is coming soon. For now, enter your
              website above — we read your pages and build the assistant from
              them.
            </p>
          )}

          {manualOpen && MANUAL_UPLOAD_READY && (
            <div className="onboarding__upload">
              <label
                className={`onboarding__dropzone${isDragging ? " is-dragging" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                <UploadIcon />
                <span className="onboarding__dropzone-title">
                  <strong>Click to upload</strong> or drag and drop
                </span>
                <span className="onboarding__dropzone-hint">PDF, DOC, DOCX, TXT up to 10MB</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileInputChange}
                  hidden
                />
              </label>

              {files.length > 0 && (
                <ul className="onboarding__file-list">
                  {files.map((file, index) => (
                    <li key={`${file.name}-${index}`}>
                      <DocIcon />
                      <div className="onboarding__file-info">
                        <strong>{file.name}</strong>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="onboarding__file-remove"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => removeFile(index)}
                      >
                        <CloseIcon />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <ul
            className={`onboarding__checklist${crawling ? " is-running" : ""}`}
            aria-live="polite"
          >
            {checklist.map((item, index) => {
              const done = crawling && index < stage;
              const active = crawling && index === stage;

              return (
                <li
                  key={item.title}
                  className={`${done ? "is-done" : ""}${active ? " is-active" : ""}`.trim()}
                >
                  <span className="onboarding__check-icon">
                    {active ? (
                      <span className="onboarding__check-spinner" aria-hidden="true" />
                    ) : (
                      <CheckIcon />
                    )}
                  </span>
                  <div>
                    <strong>{item.title}</strong>
                    <span>{active ? item.busyDetail : item.detail}</span>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* Everything the server knows about the crawl: its own message,
              the pages read against the limit, and which of the five stages
              is running. Only while it is actually working - once it is done
              this step has already moved on. */}
          {crawling && knowledge && <KnowledgeProgress state={knowledge} />}

          <p className="onboarding__eta">
            <ClockIcon />
            {crawling ? (
              <>
                Hang tight &ndash; this usually takes{" "}
                <strong>30&ndash;90 seconds</strong>
              </>
            ) : (
              <>
                Setup usually takes <strong>30&ndash;90 seconds</strong>
              </>
            )}
          </p>
        </section>

        <section
          className="onboarding__preview"
          aria-hidden={crawling || showShot ? undefined : "true"}
        >
          <div className={`onboarding__browser${crawling ? " is-scanning" : ""}`}>
            <div className="onboarding__browser-bar">
              <span className="onboarding__dot onboarding__dot--red" />
              <span className="onboarding__dot onboarding__dot--yellow" />
              <span className="onboarding__dot onboarding__dot--green" />
              <span
                className={`onboarding__browser-address${previewUrl ? " has-url" : ""}`}
                title={previewUrl || undefined}
              >
                {previewUrl && (
                  <>
                    <LockIcon />
                    <span>{previewUrl}</span>
                  </>
                )}
              </span>
            </div>
            <div
              className={`onboarding__browser-body${showShot ? " has-shot" : ""}`}
            >
              {showShot ? (
                <div
                  className={`onboarding__shot${shotState === "ready" ? " is-ready" : ""}`}
                >
                  <img
                    src={shotSrc}
                    alt={`Preview of ${previewUrl}`}
                    referrerPolicy="no-referrer"
                    onLoad={() => setShotState("ready")}
                    onError={() => setShotState("failed")}
                    className="img-fluid"
                  />
                  {shotState === "loading" && (
                    <span className="onboarding__shot-status">
                      <span
                        className="onboarding__check-spinner"
                        aria-hidden="true"
                      />
                      Loading preview&hellip;
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <div className="onboarding__browser-media" />
                  <div className="onboarding__browser-lines">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="onboarding__browser-tiles">
                    <div>
                      <StarIcon />
                    </div>
                    <div>
                      <ShieldIcon />
                    </div>
                    <div>
                      <PhoneIcon />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="onboarding__status-card">
            <p className="onboarding__status-title">
              <SparkleIcon />
              {crawling ? "Scanning your website" : "Setting up your assistant"}
            </p>
            {previewUrl && (
              <p className="onboarding__status-url" title={previewUrl}>
                <GlobeIcon />
                <span>{previewUrl}</span>
              </p>
            )}
            <ul>
              {statusSteps.map((step, index) => {
                const done = crawling && index < stage;
                // Outside the crawl this keeps the original static preview.
                const active = crawling ? index === stage : index === 0;

                return (
                  <li
                    key={step.title}
                    className={`${done ? "is-done" : ""}${active ? " is-active" : ""}`.trim()}
                  >
                    <span className="onboarding__status-dot" />
                    <div>
                      <strong>{step.title}</strong>
                      <span>{step.detail}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      </div>

      <div className="onboarding__features">
        {features.map((feature) => (
          <div className="onboarding__feature" key={feature.title}>
            <span className="onboarding__feature-icon">{feature.icon}</span>
            <div>
              <strong>{feature.title}</strong>
              <span>{feature.detail}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
